import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Row, Col, FormFeedback,
  Spinner,
} from 'reactstrap';

import { getSpecialtiesRequest, getProvincesRequest } from '@reducers/shared/SharedAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { uploadToS3 } from '@services/UploadService';
import InputField from '@shared/components/input/InputField';
import Select from '@shared/components/input/Select';
import useTranslation from '@shared/hooks/useTranslation';
import {
  CameraIcon,
} from '@shared/icons';
import FileUploader, { FileUploaderRef } from '@shared/utils/FileUploader/FileUploader';

import '../RegisterPageContentStyle.scss';

type DentistInputFieldsProps = {
  formData: Record<string, any>;
  setFormData: (e: any) => void;
  errors?: Record<string, string>; // Assuming errors correspond to field names
  setErrors: (e: any) => void;
  prefilledEmail?: string;
};

const DentistInputFields = ({
  formData, setFormData, errors, setErrors, prefilledEmail,
}: DentistInputFieldsProps) => {
  const dispatch = useDispatch<any>();
  const i18nRegistration = useTranslation('registration');
  const profilePhotoUploader = useRef<FileUploaderRef>(null);
  const i18nDentist = useTranslation('registration.types.dentist');
  // const [ specialties, setSpecialties ] = useState([]);
  const [ uploading, setUploading ] = useState(false);

  const loadSpecialties = async (inputValue: string) => {
    try {
      const result = await dispatch(getSpecialtiesRequest({ keyword: inputValue })).$promise;
      const { data } = result.data;
      const specialtyOptions = data?.map((specialty: any) => ({
        label: specialty.name,
        value: specialty.value,
        key: specialty.id,
      })) || [];
      return specialtyOptions;
    } catch (err) {
      handleError(err);
      return [];
    }
  };

  const loadProvinces = async (inputValue: string) => {
    try {
      const result = await dispatch(getProvincesRequest({ keyword: inputValue })).$promise;
      const provinceOptions = result.data?.data?.map((prov: any) => ({
        label: prov.name,
        value: prov.value,
        key: prov.id,
      })) || [];
      return provinceOptions;
    } catch (err) {
      handleError(err);
      return [];
    }
  };

  const handleFileAdded = async (res: File[]) => {
    setUploading(true);
    try {
      const file = res[0];
      const link = await uploadToS3('profile', file, {
        name: file.name,
        type: file.type,
      });
      setFormData((s: any) => ({
        ...s,
        image: link,
        profile_picture_preview: URL.createObjectURL(res[0]),
      }));
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  const onHandleSelectProvince = (val: any) => {
    setFormData({ ...formData, province: val });
    setErrors({
      ...errors,
      province: '',
    });
  };

  const onHandleSelectSpecialty = (val: any) => {
    setFormData({ ...formData, specialty: val });
    setErrors({
      ...errors,
      specialty: '',
    });
  };

  useEffect(() => {
    // loadSpecialties();
    setFormData({
      first_name: '',
      last_name: '',
      email: prefilledEmail || '',
      mobile_number: '',
      registration_no: '',
      province: '',
      specialty: '',
      password: '',
      password_confirmation: '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Row>
        <Col>
          <div className="s1 title">
            {i18nDentist.description}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="s5 basic-info-style">
            {i18nRegistration.basicInfo}
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="text-center">
          <div className="photo-uploader mx-auto" onClick={() => profilePhotoUploader.current?.open()}>
            <FileUploader
              ref={profilePhotoUploader}
              onDrop={(ac) => handleFileAdded(ac)}
              accept={{
                'image/*': [ '.jpeg', '.jpg', '.png' ],
              }}
              maxSize={1e8}
              disabled={uploading}
            />
            {formData.profile_picture_preview && (
              <img src={formData.profile_picture_preview} alt="Profile" />
            )}
            {!formData.profile_picture_preview && !uploading && (
              <CameraIcon />
            )}
            {uploading && (
              <Spinner color="primary" size="md" className="spinner" />
            )}
          </div>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col className="text-center">
          {LocaleService.parseTranslationElement(i18nDentist.profile, {
            // eslint-disable-next-line react/no-unstable-nested-components
            optional: (optional: string) => (
              <span className="logo-optional-style">{optional}</span>
            ),
          })}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <InputField
            id="input-first-name"
            name="first_name"
            label={i18nDentist.input.first_name.label}
            placeholder={i18nDentist.input.first_name.placeholder}
            type="text"
            required
            value={formData?.first_name}
            fieldError={errors?.first_name ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              first_name: value,
            }))}
          />
        </Col>
        <Col md={6}>
          <InputField
            id="input-last-name"
            name="last_name"
            label={i18nDentist.input.last_name.label}
            placeholder={i18nDentist.input.last_name.placeholder}
            type="text"
            required
            value={formData?.last_name}
            fieldError={errors?.last_name ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              last_name: value,
            }))}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-email"
            name="email"
            label={i18nDentist.input.email.label}
            placeholder={i18nDentist.input.email.placeholder}
            type="email"
            required
            value={formData?.email}
            fieldError={errors?.email ?? ''}
            isReadOnly={!!prefilledEmail}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              email: value,
            }))}
          />
          {!!prefilledEmail && (
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                marginTop: '-12px',
                marginBottom: '10px',
              }}
            >
              This is the email your invitation was sent to. Your referral
              will only appear if you register with this email.
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-mobile"
            name="mobile_number"
            label={i18nDentist.input.mobile.label}
            placeholder={i18nDentist.input.mobile.placeholder}
            type="text"
            required
            value={formData?.mobile_number}
            fieldError={errors?.mobile_number ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              mobile_number: value,
            }))}
            isMobile
            mobileCountryCode="+1"
            customClassName="custom-mobile-number-form-padding"
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="s5 basic-info-style">
            {i18nDentist.otherInfo}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-registration-no"
            name="registration_no"
            label={i18nDentist.input.registrationNumber.label}
            placeholder={i18nDentist.input.registrationNumber.placeholder}
            type="text"
            required
            value={formData?.registration_no}
            fieldError={errors?.registration_no ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              registration_no: value,
            }))}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Select
            id="input-province"
            async
            cacheOptions
            defaultOptions
            loadOptions={loadProvinces}
            classNamePrefix="react-select"
            label={i18nDentist.input.province.label}
            placeholder={i18nDentist.input.province.placeholder}
            onChange={(v: any) => onHandleSelectProvince(v.key)}
            className={classNames({
              'is-invalid': errors?.province,
              'mb-3': !errors?.province && true,
              'mb-1': errors?.province && true,
            })}
            required
            withFloatingLabel
          />
          <FormFeedback>
            {errors?.province && errors?.province.length > 0 ? errors?.province[0] : ''}
          </FormFeedback>
        </Col>
      </Row>
      <Row>
        <Col>
          <Select
            id="input-specialty"
            async
            cacheOptions
            defaultOptions
            loadOptions={loadSpecialties}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            classNamePrefix="react-select"
            label={i18nDentist.input.specialty.label}
            placeholder={i18nDentist.input.specialty.placeholder}
            onChange={(v: any) => onHandleSelectSpecialty(v)}
            className={classNames({
              'is-invalid': errors?.specialty,
              'mb-3': !errors?.specialty && true,
              'mb-1': errors?.specialty && true,
            })}
            required
            withFloatingLabel
            isMulti
          />
          <FormFeedback>
            {errors?.specialty && errors?.specialty.length > 0 ? errors?.specialty[0] : ''}
          </FormFeedback>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="s5 basic-info-style">
            {i18nRegistration.securityInfo}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-password"
            name="password"
            label={i18nDentist.input.password.label}
            placeholder={i18nDentist.input.password.placeholder}
            type="password"
            required
            value={formData?.password}
            fieldError={errors?.password ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              password: value,
            }))}
            isPasswordStrength
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <InputField
            id="input-password_confirmation"
            name="password_confirmation"
            label={i18nDentist.input.passwordConfirmation.label}
            placeholder={i18nDentist.input.passwordConfirmation.placeholder}
            type="password"
            required
            value={formData?.password_confirmation}
            fieldError={errors?.password_confirmation ?? ''}
            onInputChangeHandler={(value) => setFormData((prevState: any) => ({
              ...prevState,
              password_confirmation: value,
            }))}
          />
        </Col>
      </Row>
    </>
  );
};

export default DentistInputFields;
